import { PageHero } from "@/components/site/PageHero";
import { SectionHead } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/Motion";
import { Portrait } from "@/components/site/Portrait";
import { LinkButton } from "@/components/site/LinkButton";
import { ContactButton } from "@/components/site/ContactButton";
import { AboutInvitation } from "@/components/about/AboutInvitation";
import { STAGES } from "@/lib/content";
import { Petal } from "@/components/site/Petal";

const CREDENTIALS = [
  "Certified Mindset Coach",
  "Neuro-Linguistic Programming (NLP) Master Practitioner",
  "Mindful Embodiment & Somatic Awareness Coach",
];

const PRINCIPLES = [
  { t: "Awareness before change", d: "We start by noticing, not fixing. Seeing clearly is often the first release." },
  { t: "Your own pace", d: "Every layer is explored gently. Nothing needs to be forced or rushed." },
  { t: "Rooted in everyday life", d: "Practices are meant to fit into ordinary days, not to take you away from them." },
];

const Opening = () => (
  <section className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-24 sm:px-8 md:grid-cols-[0.85fr_1.15fr] md:gap-12 md:pb-32 lg:gap-20 lg:px-12" data-testid="about-guide">
    <Reveal className="mx-auto w-full max-w-sm md:max-w-md"><Portrait testId="about-portrait" /></Reveal>
    <Reveal delay={0.15}>
      <p className="eyebrow text-center sm:text-left">Meet your coach</p>
      <h2 className="font-display mt-4 text-center text-[3.5rem] font-medium leading-none tracking-tight text-[#221e1b] sm:text-left lg:text-[4.5rem]" data-testid="about-guide-name">Sheetal Garg</h2>
      <p className="mt-4 text-center text-base font-medium text-[#7a5615] sm:text-left sm:text-lg" data-testid="about-guide-title">Founder and Lifestyle Coach, Panchmaya</p>
      <p className="mt-7 max-w-xl text-[1.0625rem] leading-[1.8] text-[#5d544c] sm:text-lg" data-testid="about-intro">
        Sheetal Garg created Panchmaya to offer private 1:1 lifestyle coaching and an upcoming
        21-day coaching journey inspired by the five koshas. Her approach draws on her background
        in mindful living, guided visualization and embodiment practices.
      </p>
      <div className="mt-8 flex justify-center sm:justify-start">
        <ContactButton testId="about-guide-contact-btn" variant="outline" size="large" />
      </div>
    </Reveal>
  </section>
);

const Story = () => (
  <section className="bg-[#f2ebe0] py-24 md:py-32" data-testid="about-story">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20 lg:px-12">
      <SectionHead size="large" eyebrow="The story behind Panchmaya" title={<>Why <em>Panchmaya</em> exists</>} />
      <Reveal delay={0.1} className="max-w-2xl space-y-6 text-[1.0625rem] leading-[1.8] text-[#5d544c] sm:text-lg">
        <p>
          <span className="font-display text-2xl text-[#221e1b]">Panch</span> is Sanskrit for five, and{" "}
          <span className="font-display text-2xl text-[#7a5615]">-maya</span>, meaning &quot;made of&quot;, ends the name of each of the five koshas:
          Annamaya, Pranamaya, Manomaya, Vijnanamaya and Anandamaya, the layers through which we move from outer
          experience to the inner Self.
        </p>
        <p>
          The mark carries the same story: a circle moving from dark to light, five
          petals for the five layers, and a golden centre for the Self that was always there.
        </p>
        <p data-testid="about-story-draft">
          Through Panchmaya, Sheetal brings her coaching background together with the ancient map of the five koshas, offering
          a gentle, structured way to perceive how you experience the world, release some of what you carry, and
          reconnect with yourself.
        </p>
      </Reveal>
    </div>
  </section>
);

const Approach = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-32 lg:px-12" data-testid="about-approach">
    <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
      <SectionHead size="large" eyebrow="The approach" title="Gentle, layered, practical"
        text="Panchmaya works with five layers of experience: the body, the breath, the thinking and feeling mind, a deeper awareness, and a quiet sense of connection. The journey invites you to notice what you carry, let some of it go, and return to yourself." />
      <div className="flex justify-center sm:justify-start">
        <LinkButton to="/journey" variant="text" testId="about-approach-journey-link">See the full journey</LinkButton>
      </div>
    </div>
    <Reveal className="mt-10 flex flex-wrap justify-center gap-3 sm:justify-start">
      {STAGES.map((s) => (
        <span key={s.n} className="inline-flex items-center gap-2.5 rounded-full border border-[#e4dacb] bg-[#fffaf3] px-4 py-2 text-base text-[#221e1b]">
          <Petal color={s.color} size={16} /> {s.name}
        </span>
      ))}
    </Reveal>
    <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-3">
      {PRINCIPLES.map((p, i) => (
        <Reveal key={p.t} delay={i * 0.1} className="rounded-[1.75rem] border border-[#e4dacb] bg-[#fffaf3] p-7 sm:p-8">
          <span className="font-display text-5xl text-[#7a5615]">0{i + 1}</span>
          <h3 className="font-display mt-5 text-[2.125rem] font-medium leading-tight text-[#221e1b]" data-testid={`principle-${i + 1}`}>{p.t}</h3>
          <p className="mt-4 text-[1.0625rem] leading-[1.75] text-[#5d544c] sm:text-lg">{p.d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Background = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12" data-testid="about-background">
    <div className="grid gap-10 border-t border-[#e4dacb] pt-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
      <SectionHead size="large" eyebrow="Experience and background" title="Training and practice" />
      <Reveal>
        <ul className="divide-y divide-[#e4dacb] border-y border-[#e4dacb]" data-testid="about-background-list">
          {CREDENTIALS.map((c, i) => (
            <li key={c} className="flex items-start gap-4 py-6" data-testid={`credential-${i + 1}`}>
              <span aria-hidden="true" className="mt-4 h-2 w-2 shrink-0 rounded-full bg-[#7a5615] sm:mt-[1.125rem]" />
              <span className="font-display text-[1.75rem] font-medium leading-snug text-[#221e1b] sm:text-[2rem]">{c}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero testId="about-hero" eyebrow="About"
        lines={["The person", <em key="b" className="text-[#7a5615]">behind the path.</em>]}
        text="Meet Sheetal Garg, founder and lifestyle coach at Panchmaya, and discover the ideas that shape the journey.">
        <LinkButton to="/journey" testId="about-hero-journey-btn">Explore the Journey</LinkButton>
        <ContactButton testId="about-hero-contact-btn" variant="outline" size="large" />
      </PageHero>
      <Opening />
      <Story />
      <Approach />
      <Background />
      <AboutInvitation />
    </div>
  );
}
